import {
  AutomationDashboardSummaryMock,
  AutomationResultsMock,
} from '../charts/chart-types';
import {
  AutomationResultsBreakdown,
  AutomationResultsChartProps,
} from './automation-results-chart';

/**
 * Maps a results-breakdown key onto its donut key. Covers the seven
 * subcategories the analytics service returns plus its uncategorized count;
 * the first four are also the keys the POC fixtures use.
 */
const BREAKDOWN_KEYS: {
  [subcategory: string]: keyof AutomationResultsBreakdown;
} = {
  automated_high_confidence: 'autoHigh',
  automated_rule: 'autoRule',
  manual_low_confidence: 'manualLow',
  manual_rule: 'manualRule',
  manual_ineligible: 'manualIneligible',
  manual_not_valid_for_prediction: 'manualNotValid',
  manual_other: 'manualOther',
  uncategorized: 'uncategorized',
};

/**
 * Only keys present on the payload are carried through, so a payload with
 * fewer buckets yields a breakdown with fewer keys rather than zero-filled
 * ones.
 */
const toBreakdown = (a: AutomationResultsMock): AutomationResultsBreakdown => {
  const breakdown: AutomationResultsBreakdown = {};
  Object.keys(a).forEach(subcategory => {
    const key = BREAKDOWN_KEYS[subcategory];
    if (key) {
      breakdown[key] = a[subcategory];
    }
  });
  return breakdown;
};

export interface ToAutomationResultsChartOptions {
  /** Override the donut size. */
  size?: number;
  /** Override the center label total — useful when totals come from `kpis.current`. */
  totalCasesLabel?: string;
  hideLegend?: boolean;
  ariaLabel?: string;
}

/**
 * Map an automation summary response into `<AutomationResultsChart>` props.
 * Renames the payload's `snake_case` buckets to the donut's `camelCase` keys.
 */
export const toAutomationResultsChartProps = (
  response: AutomationDashboardSummaryMock,
  options: ToAutomationResultsChartOptions = {},
): AutomationResultsChartProps => ({
  breakdown: toBreakdown(response.data.automation_results),
  size: options.size,
  totalCasesLabel: options.totalCasesLabel,
  hideLegend: options.hideLegend,
  ariaLabel: options.ariaLabel ?? 'Automation results',
});
